import { CheckIcon } from "@phosphor-icons/react";
import { Fragment } from "react";
import { cn } from "../../../utils";
import { isStepClickable, resolveStepStatuses, StepperStep, StepStatus } from "./stepper.utils";

export type { StepperStep, StepStatus } from "./stepper.utils";

interface StepperProps {
  steps: StepperStep[];
  currentStepId: string;
  onStepClick?: (stepId: string) => void;
  className?: string;
  ariaLabel?: string;
}

const dotClassNames: Record<StepStatus, string> = {
  current: "bg-primary-500 text-white ring-4 ring-primary-100",
  completed: "bg-primary-100 text-primary-700",
  upcoming: "border border-gray-300 bg-white text-gray-500",
};

const labelClassNames: Record<StepStatus, string> = {
  current: "text-primary-700",
  completed: "text-gray-700",
  upcoming: "text-gray-400",
};

const Stepper = ({
  steps,
  currentStepId,
  onStepClick,
  className,
  ariaLabel = "Étapes",
}: StepperProps) => {
  const resolvedSteps = resolveStepStatuses(steps, currentStepId);
  const lastIndex = resolvedSteps.length - 1;

  return (
    <nav aria-label={ariaLabel} className={className}>
      <ol className="flex items-start">
        {resolvedSteps.map(({ step, status, index }) => {
          const clickable = isStepClickable(status, Boolean(onStepClick));
          const dotClassName = cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors",
            dotClassNames[status],
            clickable &&
              "hover:bg-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
          );
          const dotContent =
            status === "completed" ? (
              <CheckIcon weight="bold" className="size-4" aria-hidden />
            ) : (
              index + 1
            );

          return (
            <Fragment key={step.id}>
              <li
                className="flex flex-col items-center gap-1.5"
                aria-current={status === "current" ? "step" : undefined}
              >
                {clickable ? (
                  <button
                    type="button"
                    className={dotClassName}
                    onClick={() => onStepClick?.(step.id)}
                    aria-label={`Revenir à l'étape ${step.label}`}
                  >
                    {dotContent}
                  </button>
                ) : (
                  <span className={dotClassName}>{dotContent}</span>
                )}
                <span
                  className={cn(
                    "whitespace-nowrap text-xs font-semibold",
                    labelClassNames[status],
                    // Only the current label fits on a phone; the others come back from md up
                    status !== "current" && "hidden md:block",
                  )}
                >
                  {step.label}
                </span>
              </li>
              {index < lastIndex && (
                <li
                  aria-hidden
                  className={cn(
                    "mt-3.5 h-px flex-1 transition-colors",
                    status === "completed" ? "bg-primary-300" : "bg-gray-200",
                  )}
                />
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export { Stepper };
